import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ApproverDecision, DecisionBatch, TransitionRejection } from '@zevik/schemas';
import { type BatchDraft, buildBatchRequest } from '../review/batch';
import { buildDecisionRequest, type DecisionDraft } from '../review/decision';
import { initialSelection } from '../review/order';
import {
  type FindingEntry,
  loadWorkspace,
  recordBatch,
  recordDecision,
  type Source,
  type Workspace as WorkspaceData,
} from '../api/client';
import { approve } from '../api/pipeline';
import { apiSentence, SESSION_LOST_SENTENCE, sessionLost } from '../credential/session';
import { FindingList } from './FindingList';
import { FindingDetail } from './FindingDetail';
import { Absences } from './Absences';
import { GateControl } from './GateControl';

/**
 * The reviewer's workspace: read a finding, check what it rests on, decide
 * (docs/DECISIONS.md ZD-112).
 *
 * This serves the read-and-decide subset of the fourteen actions ZD-111 §2 derived
 * from what the human gate refuses on, and — since `sds-12o` — the gate itself
 * (`GateControl`, ZD-125 §3): a press, and a refusal that names the findings it
 * is about by title. What it does not serve is declared on screen by
 * `Absences` rather than left as a blank.
 *
 * Since `sds-12l3` it is one screen of the assessment page rather than the
 * whole console: the page owns who is signed in and what happens when the
 * session ends (`credential/session.ts`), and this reports a lost session
 * upward through `onSessionLost` — `load` where nothing was read yet, so the
 * page can stand a sign-in in its place, and `record` where a decision was
 * refused, so the page keeps this mounted with the draft in it and a sign-in
 * above. `reloadToken` is bumped by the page after a sign-in, so the record
 * is re-read in case it moved while the session was gone.
 */
export function ReviewScreen({
  source,
  assessmentId,
  sessionRestored,
  reloadToken,
  onSessionLost,
  onLoaded,
  onApproved,
}: {
  source: Source;
  assessmentId: string;
  /** Every successful sign-in from the panel, so a stale refusal under a draft is cleared. */
  sessionRestored: number;
  reloadToken: number;
  onSessionLost: (why: 'load' | 'record', reason: string) => void;
  /** The number of findings, for the page's own words. */
  onLoaded?: (workspace: WorkspaceData) => void;
  /** Through the gate: the page re-reads `next`, and this screen is replaced by what it says. */
  onApproved: () => Promise<void>;
}): React.JSX.Element {
  const [workspace, setWorkspace] = useState<WorkspaceData | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const loaded = await loadWorkspace(source, assessmentId);
    setWorkspace(loaded);
    /*
     * The first row a person sees, by the one ordering the list also reads
     * (`review/order.ts`). Seeding from the API's order put the highlight and
     * the pane on two different findings until a click (ZD-123 §11).
     */
    setSelected((current) => current ?? initialSelection(loaded.findings));
    onLoaded?.(loaded);
  }, [source, assessmentId, onLoaded]);

  useEffect(() => {
    let live = true;
    load().catch((error: unknown) => {
      if (!live) return;
      if (sessionLost(error)) {
        onSessionLost('load', apiSentence(error));
        return;
      }
      setFailure(error instanceof Error ? error.message : String(error));
    });
    return () => {
      live = false;
    };
  }, [load, reloadToken, onSessionLost]);

  /*
   * Keyed on `finding_key`, because that is what a decision follows: a re-run
   * mints a new `finding_id` and the reproducibility tuple is what carries a
   * person's recorded judgement across it (ZD-061 §3). The map is built from the
   * server's `standing` and never re-derived here.
   */
  const standing = useMemo<ReadonlyMap<string, ApproverDecision>>(() => {
    const map = new Map<string, ApproverDecision>();
    for (const decision of workspace?.reviewLog.standing ?? []) {
      map.set(decision.finding_key, decision);
    }
    return map;
  }, [workspace]);

  /*
   * The groups, by id, so a standing decision made in one can show the
   * group's reason beside it (ZD-127 §3): a batched decision may carry no
   * reason of its own, and a screen showing it without the group's would be
   * a batch hiding what it contains.
   */
  const batches = useMemo<ReadonlyMap<string, DecisionBatch>>(() => {
    const map = new Map<string, DecisionBatch>();
    for (const batch of workspace?.reviewLog.batches ?? []) map.set(batch.batch_id, batch);
    return map;
  }, [workspace]);

  const current = workspace?.findings.find((entry) => entry.finding.finding_id === selected);

  /*
   * **Back to the top when the selection changes**, which reading the output is
   * what found: picking a finding from the list left the pane where the last one
   * had been scrolled to, so a reviewer working down forty findings met the tail
   * of each — *what this screen does not do yet* — instead of the finding. Every
   * test passed, and would have.
   */
  const pane = useRef<HTMLElement>(null);
  useEffect(() => {
    pane.current?.scrollTo({ top: 0 });
  }, [selected]);

  const onRecord = useCallback(
    async (draft: DecisionDraft): Promise<void> => {
      if (current === undefined) return;
      try {
        await recordDecision(
          source,
          assessmentId,
          buildDecisionRequest(current.finding, current, draft, current.statement),
        );
      } catch (error) {
        if (sessionLost(error)) {
          onSessionLost('record', apiSentence(error));
          throw new Error(SESSION_LOST_SENTENCE);
        }
        throw error;
      }
      // Re-read rather than patch the local copy: `standing` is the server's
      // resolution of the history, and a workspace splicing its own answer in
      // would be that rule expressed a second time (ZD-111 §3).
      await load();
    },
    [current, source, assessmentId, load, onSessionLost],
  );

  /*
   * A group decision: one verdict for a selection, sent as `{ batch,
   * decisions }` and judged by the API as a set (ZD-127 §2). A refusal is
   * thrown as the API's issues and the bar relays them by title; the record is
   * re-read afterwards for the reason `onRecord` gives.
   */
  const onRecordBatch = useCallback(
    async (selection: readonly FindingEntry[], draft: BatchDraft): Promise<void> => {
      const request = buildBatchRequest(selection, draft);
      try {
        await recordBatch(source, assessmentId, request.batch, request.decisions);
      } catch (error) {
        if (sessionLost(error)) {
          onSessionLost('record', apiSentence(error));
          throw new Error(SESSION_LOST_SENTENCE);
        }
        throw error;
      }
      await load();
    },
    [source, assessmentId, load, onSessionLost],
  );

  /*
   * The gate. A refusal is returned rather than thrown, because it is the
   * screen's content and not a failure; through the gate, the page re-reads
   * `next` and this screen is replaced by the report screen.
   */
  const onApprove = useCallback(async (): Promise<TransitionRejection | null> => {
    let outcome;
    try {
      outcome = await approve(source, assessmentId);
    } catch (error) {
      if (sessionLost(error)) {
        onSessionLost('record', apiSentence(error));
        throw new Error(SESSION_LOST_SENTENCE);
      }
      throw error;
    }
    if (outcome.kind === 'refused') return outcome.rejection;
    await onApproved();
    return null;
  }, [source, assessmentId, onSessionLost, onApproved]);

  if (failure !== null) {
    return (
      <main className="centre">
        <div className="centre__inner">
          <h1>This assessment could not be read</h1>
          <p>{failure}</p>
        </div>
      </main>
    );
  }

  if (workspace === null) {
    return (
      <main className="centre">
        <p className="centre__inner">Reading the findings…</p>
      </main>
    );
  }

  return (
    <div className="split">
      <FindingList
        findings={workspace.findings}
        standing={standing}
        selected={selected}
        onSelect={setSelected}
        onRecordBatch={source.base === null ? null : onRecordBatch}
      />
      <main className="detail" ref={pane}>
        {current === undefined ? (
          <p className="absent">
            This run produced no findings. That is a result rather than an error — and the
            assessment state above says whether the analysis has run at all.
          </p>
        ) : (
          <FindingDetail
            finding={current}
            standing={standing.get(current.finding_key)}
            batch={(() => {
              const id = standing.get(current.finding_key)?.batch_id ?? null;
              return id === null ? undefined : batches.get(id);
            })()}
            clarifications={workspace.clarifications}
            onRecord={onRecord}
            sessionRestored={sessionRestored}
            recordingUnavailable={
              source.base === null
                ? 'This workspace is reading a committed fixture (?fixture), so a decision ' +
                  'cannot be recorded. Open it on a deployment without that parameter to record one.'
                : null
            }
          />
        )}
        <div className="detail__inner">
          <GateControl
            findings={workspace.findings}
            standing={standing}
            onApprove={onApprove}
            onSelect={setSelected}
            unavailable={
              source.base === null
                ? 'This workspace is reading a committed fixture (?fixture), so the gate cannot be ' +
                  'asked. Open it on a deployment without that parameter.'
                : null
            }
          />
          <Absences />
        </div>
      </main>
    </div>
  );
}
